import { useLayoutEffect, useEffect, useRef, useState } from "react";
import {
  Form,
  Modal,
  OverlayTrigger,
  Table,
  ToggleButtonGroup,
  ToggleButton,
  Tooltip,
} from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { useForm, FormProvider } from "react-hook-form";
import SVG from "react-inlinesvg";
import { useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import classNames from "classnames";
import { ErrorMessage } from "@hookform/error-message";
import moment from "moment";

import { toAbsoluteUrl } from "../../../../_metronic/_helpers";
import {
  Card,
  CardBody,
  CardHeader,
} from "../../../../_metronic/_partials/controls";
import SearchWithdrawRequests from "../SearchWalletTransactions";
import {
  getBaseImageURL,
  getWalletCSSClassName,
  getWalletStatusCodes,
  decryptedData,
} from "../../../../helpers";
import { formatCurrency } from "../../../../helpers/numberFormat";
import * as actions from "../../../../store/actions/index";
import PaymentGatewayLogo from "../../../components/common/PaymentGatewaysLogo/PaymentGatewaysLogo";
import SortDropdownHeader, {
  sortData,
} from "../../../components/common/SortDropdownHeader/SortDropdownHeader";

function WithdrawRequests() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const globalImagePath = useRef(getBaseImageURL());
  const intl = useIntl();
  const methods = useForm({ mode: "onChange" });
  const {
    register: modalRegister,
    formState: { errors: modalErrors },
    reset: modalReset,
    handleSubmit: modalHandleSubmit,
    setValue: modalSetValue,
  } = useForm({ mode: "onChange" });

  const [show, setShow] = useState(false);
  const [showMore, setShowMore] = useState(2);
  const [searchData, setSearchData] = useState({ transactionType: 2 });
  const [selectedTransactionInfo, setSelectedTransactionInfo] = useState();
  const [selectedTransactionStatus, setSelectedTransactionStatus] = useState();
  const [sortConfig, setSortConfig] = useState({
    column: null,
    direction: null,
  });
  const isEncryption = useRef(
    import.meta.env.VITE_DATABASE_ENCRYPTION === "true"
  );

  useEffect(() => {
    dispatch(actions.getWalletTransactions(1, searchData));
     
  }, [dispatch]);

  const { loadingTransactions, isSpinning, walletTransactions } = useSelector(
    (state) => state.wallet
  );
  const { generalSettings } = useSelector((state) => state.settings);
  const { currentPermissions } = useSelector((state) => state.role);
  useLayoutEffect(() => {
    if (generalSettings?.walletModule === "no") {
      navigate(`/`);
    }
     
  }, [generalSettings?.walletModule]);

  const handleClose = () => {
    modalReset();
    setSelectedTransactionInfo();
    setSelectedTransactionStatus();
    setShow(false);
  };

  const loadMore = () => {
    setShowMore(showMore + 1);
    dispatch(actions.getWalletTransactions(showMore, searchData));
  };

  const onSubmit = (data) => {
    data.transactionType = 2;
    window.scrollTo({ top: 0, behavior: "smooth" });
    setShowMore(1);
    setSearchData(data);
    dispatch(actions.getWalletTransactions(1, data));
  };

  const onUpdateTransactionSubmit = (data) => {
    data.status = selectedTransactionStatus;
    dispatch(
      actions.updateWalletTransaction(
        selectedTransactionInfo._id,
        data,
        showMore,
        searchData
      )
    );
    handleClose();
  };

  const sortOptions = {
    walletType: (item) => item?.walletType?.toLowerCase?.(),
    "user.fullName": (item) => item?.user?.fullName?.toLowerCase?.() || "",
    createdAt: (item) => new Date(item?.createdAt),
    transactionNumber: (item) => item?.transactionNumber?.toLowerCase?.() || "",
    amount: (item) => parseFloat(item?.amount ?? 0),
    "feesDetails.transactionFees": (item) =>
      parseFloat(item?.feesDetails?.transactionFees ?? 0),
    "gatewayId.title": (item) => item?.gatewayId?.title?.toLowerCase?.() || "",
    status: (item) => parseInt(item?.status ?? 0),
  };

  const sortedWithdrawRequests = walletTransactions?.docs
    ? sortData(walletTransactions?.docs, sortConfig, sortOptions)
    : [];

  const handleSort = (column, direction) => {
    setSortConfig({ column, direction });
  };

  const walletTransactionList =
    sortedWithdrawRequests?.length > 0 ? (
      sortedWithdrawRequests?.map((transaction, key) => (
        <tr key={key}>
          <td>
            {" "}
            {transaction?.walletType === "CREDIT" ? (
              <span className="d-flex justify-content-center align-items-center rounded-fix py-1-5 px-3 fw-bold text-white user-select-none bg-success-light">
                <SVG
                  className="me-1"
                  height={12}
                  width={12}
                  src={toAbsoluteUrl("media/svg/icons/General/credit.svg")}
                />
                Credit
              </span>
            ) : (
              <span className="d-flex justify-content-center align-items-center rounded-fix py-1-5 fw-bold px-3 text-white user-select-none bg-danger">
                <SVG
                  className="me-1"
                  height={12}
                  width={12}
                  src={toAbsoluteUrl("media/svg/icons/General/debit.svg")}
                />
                Debit
              </span>
            )}
          </td>
          <td>
            {isEncryption.current
              ? `${decryptedData(
                  transaction?.user?.fullName?.split(" ")[0]
                )} ${decryptedData(transaction?.user?.fullName?.split(" ")[1])}`
              : transaction?.user?.fullName}
          </td>
          <td>
            {moment(transaction?.createdAt).format(
              generalSettings.dateFormat
            ) ?? "-"}
          </td>
          <td>
            {isEncryption.current
              ? decryptedData(transaction?.transactionNumber)
              : transaction?.transactionNumber}
          </td>
          <td
            className={
              transaction?.walletType === "CREDIT"
                ? "text-success"
                : "text-danger"
            }
            style={{
              maxWidth: "15rem",
              wordBreak: "break-all",
            }}>
            {formatCurrency(
              transaction?.amount ?? 0,
              generalSettings?.currencySymbolSide,
              transaction?.currencyId?.symbol,
              transaction?.currencyId?.code,
              generalSettings?.decimalPoints
            )}
          </td>
          <OverlayTrigger
            key={key}
            placement={"right"}
            overlay={
              <Tooltip>
                <span className="row p-1">
                  Transaction Fees:{" "}
                  <b>
                    {" "}
                    {transaction?.feesDetails?.transactionFees.toFixed(2) ?? 0}
                  </b>
                </span>
                <span className="row p-1">
                  Gateway Fee Percentage:
                  <b> {transaction?.feesDetails?.feesPercentage.toFixed(2)}</b>
                </span>
                <span className="row p-1">
                  Gateway Flat Fee:
                  <b> {transaction?.feesDetails?.flatFees.toFixed(2)}</b>
                </span>
              </Tooltip>
            }>
            <td className="text-nowrap">
              {formatCurrency(
                transaction?.feesDetails?.transactionFees ?? 0,
                generalSettings?.currencySymbolSide,
                transaction?.currencyId?.symbol,
                transaction?.currencyId?.code,
                generalSettings?.decimalPoints
              )}
            </td>
          </OverlayTrigger>
          <td>
            {" "}
            <PaymentGatewayLogo
              gatewayName={transaction?.gatewayId?.title}
              doneFromWallet={null}
            />
          </td>

          <td>
            <span
              className={`label label-lg ${getWalletCSSClassName(
                transaction?.status
              )} label-inline`}>
              {getWalletStatusCodes(transaction?.status)}
            </span>
          </td>
          <td>
            <>
              {currentPermissions?.view_withdraw_request && (
                <OverlayTrigger
                  placement="bottom"
                  popperConfig={{
                    modifiers: {
                      preventOverflow: {
                        enabled: false,
                      },
                    },
                  }}
                  overlay={<Tooltip>Edit</Tooltip>}>
                  <button
                    onClick={() => {
                      setSelectedTransactionInfo(transaction);
                      setSelectedTransactionStatus(transaction?.status);
                      modalSetValue("rejectReason", transaction?.rejectReason);
                      setShow(true);
                    }}
                    className={`action-btn-secondary me-2 ${
                      transaction?.status === 0 ? "symbol" : ""
                    }`}>
                    <span className="btn-svg">
                      <SVG
                        title=" "
                        src={toAbsoluteUrl(
                          "media/svg/icons/General/Visible.svg"
                        )}
                      />
                    </span>
                    <i className="symbol-badge bg-danger" />
                  </button>
                </OverlayTrigger>
              )}
            </>
          </td>
        </tr>
      ))
    ) : (
      <tr className="text-center">
        <td colSpan={10}>
          <p>
            {loadingTransactions ? (
              "Loading..."
            ) : (
              <span>
                <img
                  height={120}
                  src={toAbsoluteUrl("media/animations/nodata-animation.gif")}
                  alt="no data found!"
                />
                <br />
                <span className="text-primary fs-6 fw-bold">
                  No Data Available!
                </span>
              </span>
            )}
          </p>
        </td>
      </tr>
    );

  return (
    <>
      <FormProvider {...methods}>
        <Form
          name="SearchWithdrawRequests"
          onSubmit={methods.handleSubmit(onSubmit)}>
          <SearchWithdrawRequests searchData={{ transactionType: 2 }} />
        </Form>
      </FormProvider>
      <Card className="mt-4">
        <CardHeader title="Wallet Transaction List"></CardHeader>
        <CardBody className="px-4 py-6 pt-0">
          <div className="sticky-table-header-wrapper">
            <Table className="table-head-custom table-custom-bg table-vertical-center">
              <thead>
                <tr>
                  <th>
                    <SortDropdownHeader
                      columnKey="walletType"
                      onSort={handleSort}
                      currentSort={sortConfig}>
                      Type
                    </SortDropdownHeader>
                  </th>

                  <th>
                    <SortDropdownHeader
                      columnKey="user.fullName"
                      onSort={handleSort}
                      currentSort={sortConfig}>
                      User
                    </SortDropdownHeader>
                  </th>

                  <th>
                    <SortDropdownHeader
                      columnKey="createdAt"
                      onSort={handleSort}
                      currentSort={sortConfig}>
                      Date
                    </SortDropdownHeader>
                  </th>

                  <th>
                    <SortDropdownHeader
                      columnKey="transactionNumber"
                      onSort={handleSort}
                      currentSort={sortConfig}>
                      Transaction ID
                    </SortDropdownHeader>
                  </th>

                  <th>
                    <SortDropdownHeader
                      columnKey="amount"
                      onSort={handleSort}
                      currentSort={sortConfig}>
                      Amount
                    </SortDropdownHeader>
                  </th>

                  <th>
                    <SortDropdownHeader
                      columnKey="feesDetails.transactionFees"
                      onSort={handleSort}
                      currentSort={sortConfig}>
                      Fees
                    </SortDropdownHeader>
                  </th>

                  <th>
                    <SortDropdownHeader
                      columnKey="gatewayId.title"
                      onSort={handleSort}
                      currentSort={sortConfig}>
                      Gateway
                    </SortDropdownHeader>
                  </th>

                  <th>
                    <SortDropdownHeader
                      columnKey="status"
                      align="right"
                      onSort={handleSort}
                      currentSort={sortConfig}>
                      Status
                    </SortDropdownHeader>
                  </th>

                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>{walletTransactionList}</tbody>
            </Table>
            {walletTransactions?.displayLoadMore ? (
              <div className="mt-5 text-center">
                <button
                  type="button"
                  className="btn btn-dark py-2"
                  onClick={() => loadMore()}
                  disabled={isSpinning}>
                  {isSpinning ? (
                    <span
                      className="spinner-border spinner-border-sm"
                      role="status"
                      aria-hidden="true"></span>
                  ) : (
                    "Load More"
                  )}
                </button>
              </div>
            ) : null}
          </div>
        </CardBody>
      </Card>
      <Modal
        show={show}
        size={
          selectedTransactionInfo?.walletType === "DEBIT" &&
          selectedTransactionInfo?.bankName
            ? "lg"
            : "md"
        }
        onHide={handleClose}>
        <Modal.Header>
          <Modal.Title>
            <div className="d-flex align-items-center">
              <div className="me-2">
                <img
                  className="rounded-circle"
                  height={50}
                  width={50}
                  alt="user"
                  src={
                    selectedTransactionInfo?.user?.photo
                      ? `${globalImagePath.current}${selectedTransactionInfo?.user?.photo}`
                      : toAbsoluteUrl("media/users/default.jpg")
                  }
                />
              </div>
              <div>
                <a href="#" className="fw-bold font-size-h5 text-secondary">
                  {isEncryption.current
                    ? decryptedData(
                        selectedTransactionInfo?.user?.fullName?.split(" ")[0]
                      ) +
                      " " +
                      decryptedData(
                        selectedTransactionInfo?.user?.fullName?.split(" ")[1]
                      )
                    : selectedTransactionInfo?.user?.fullName}
                </a>

                <a href={undefined} className="d-flex align-items-center">
                  <span className="d-flex align-items-center bg-primary p-1 rounded-1 me-1">
                    <SVG
                      height={12}
                      width={12}
                      src={toAbsoluteUrl(
                        "media/svg/icons/General/email.svg"
                      )}></SVG>
                  </span>
                  <span className="font-size-xs text-primary">
                    {isEncryption.current
                      ? decryptedData(selectedTransactionInfo?.user?.email)
                      : selectedTransactionInfo?.user?.email}
                  </span>
                </a>
              </div>
            </div>
          </Modal.Title>
          <button
            type="button"
            className="btn btn-sm btn-dark py-1 btn-hover-dark ms-auto"
            onClick={handleClose}>
            Close
          </button>
        </Modal.Header>
        <Modal.Body className="px-4 py-6 pt-0">
          <div
            className={
              selectedTransactionInfo?.walletType === "DEBIT" &&
              selectedTransactionInfo?.bankName
                ? "d-flex align-items-start gap-5"
                : ""
            }>
            {selectedTransactionInfo?.gatewayId?.title === "Offline" ? (
              <>
                <div className="bg-input-bg-clr p-4 rounded mb-5">
                  <h5 className="text-black mb-3 font-size-sm fw-bold">
                    Acknowledgement Document
                  </h5>
                  <ul className="p-0 m-0">
                    <li className="d-flex justify-content-between align-items-center rounded text-black p-2 bg-white mb-2">
                      <span>Document</span>
                      <strong className="ms-4">
                        <a
                          href={`${globalImagePath.current}${selectedTransactionInfo?.acknowledgeDocument}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn btn-sm bg-secondary p-1 px-2 text-white rounded d-flex align-items-center justify-content-center gap-2"
                          download>
                          <span className="d-flex align-items-center justify-content-center gap-1 font-size-xs">
                            Download
                            <SVG
                              title=" "
                              path="black"
                              src={toAbsoluteUrl(
                                "media/svg/icons/Files/DownloadedFile.svg"
                              )}
                            />
                          </span>
                        </a>
                      </strong>
                    </li>
                  </ul>
                </div>
              </>
            ) : null}
            {selectedTransactionInfo?.walletType === "DEBIT" &&
            selectedTransactionInfo?.bankName ? (
              <div className="w-100 bg-input-bg-clr p-4 rounded">
                <h5 className="text-black mb-3 fw-bold">Bank Details</h5>
                <ul className="p-0 m-0">
                  {selectedTransactionInfo?.bankName ? (
                    <li className="d-flex justify-content-between align-items-center rounded text-black p-2 bg-white mb-2">
                      <span>Bank Name</span>
                      <strong className="ms-4 text-primary">
                        {decryptedData(selectedTransactionInfo?.bankName)}
                      </strong>
                    </li>
                  ) : null}
                  {selectedTransactionInfo?.accountHolderName ? (
                    <li className="d-flex justify-content-between align-items-center rounded text-black p-2 bg-white mb-2">
                      <span>Account Holder Name</span>
                      <strong className="ms-4 text-primary">
                        {decryptedData(
                          selectedTransactionInfo?.accountHolderName
                        )}
                      </strong>
                    </li>
                  ) : null}
                  {selectedTransactionInfo?.accountType ? (
                    <li className="d-flex justify-content-between align-items-center rounded text-black p-2 bg-white mb-2">
                      <span>Account Type</span>
                      <strong className="ms-4 text-primary">
                        {selectedTransactionInfo?.accountType}
                      </strong>
                    </li>
                  ) : null}
                  {selectedTransactionInfo?.accountNumber ? (
                    <li className="d-flex justify-content-between align-items-center rounded text-black p-2 bg-white mb-2">
                      <span>Account Number</span>
                      <strong className="ms-4 text-primary">
                        {decryptedData(selectedTransactionInfo?.accountNumber)}
                      </strong>
                    </li>
                  ) : null}
                  {selectedTransactionInfo?.routingNumber ? (
                    <li className="d-flex justify-content-between align-items-center rounded text-black p-2 bg-white">
                      <span>Routing Number</span>
                      <strong className="ms-4 text-primary">
                        {decryptedData(selectedTransactionInfo?.routingNumber)}
                      </strong>
                    </li>
                  ) : null}
                </ul>
              </div>
            ) : null}
            <Form
              name="withdrawRequests"
              className="w-100"
              onSubmit={modalHandleSubmit(onUpdateTransactionSubmit)}>
              <div className="w-100 bg-input-bg-clr p-4 rounded">
                <h5 className="text-black mb-3 fw-bold">Payment Details</h5>
                <ul className="p-0 m-0">
                  <li className="d-flex justify-content-between align-items-center rounded text-black p-2 bg-white mb-2">
                    <span>Wallet ID</span>
                    <strong className="ms-4 text-primary">
                      {/* {isEncryption.current
                      ? decryptedData(selectedTransactionInfo?.walletId) ?? "-"
                      : selectedTransactionInfo?.walletId ?? "-"} */}
                      {selectedTransactionInfo?.walletId}
                    </strong>
                  </li>
                  <li className="d-flex justify-content-between align-items-center rounded text-black p-2 bg-white mb-2">
                    <span>Amount</span>
                    <strong className="ms-4 text-primary">
                      {formatCurrency(
                        selectedTransactionInfo?.amount,
                        generalSettings.currencySymbolSide,
                        selectedTransactionInfo?.currencyId?.symbol,
                        selectedTransactionInfo?.currencyId?.code,
                        generalSettings.decimalPoints
                      )}
                    </strong>
                  </li>
                  <li className="d-flex justify-content-between align-items-center rounded text-black p-2 bg-white mb-2">
                    <span>Transaction Fees:</span>
                    <strong className="ms-4 text-primary">
                      {formatCurrency(
                        selectedTransactionInfo?.feesDetails?.transactionFees ??
                          0,
                        generalSettings?.currencySymbolSide,
                        selectedTransactionInfo?.currencyId?.symbol,
                        selectedTransactionInfo?.currencyId?.code,
                        generalSettings?.decimalPoints
                      )}
                    </strong>
                  </li>
                  <li className="d-flex justify-content-between align-items-center rounded text-black p-2 bg-white mb-2">
                    <span>Status</span>
                    <span
                      className={`label label-lg ${getWalletCSSClassName(
                        selectedTransactionInfo?.status
                      )} label-inline`}>
                      {getWalletStatusCodes(selectedTransactionInfo?.status)}
                    </span>
                  </li>

                  <li className="d-flex justify-content-between align-items-center rounded text-black p-2 bg-white">
                    <span>Action</span>
                    <ToggleButtonGroup type="radio" name="status">
                      {currentPermissions?.approve_withdraw_request && (
                        <ToggleButton
                          className={`font-size-xs font-size-xs me-2 badge-success text-white
                            ${
                              selectedTransactionStatus === 2 &&
                              "badge-success-active"
                            }
                            `}
                          size="sm"
                          onClick={() => setSelectedTransactionStatus(2)}
                          disabled={selectedTransactionInfo?.status === 1}>
                          <span>
                            <SVG
                              height={16}
                              width={16}
                              src={toAbsoluteUrl(
                                "media/svg/icons/Navigation/Check.svg"
                              )}
                            />
                          </span>{" "}
                          Approve
                        </ToggleButton>
                      )}
                      {currentPermissions?.reject_withdraw_request && (
                        <ToggleButton
                          variant="danger"
                          className={`font-size-xs font-size-xs badge-danger text-white 
                            ${
                              selectedTransactionStatus === 3 &&
                              "badge-danger-active"
                            }
                            `}
                          size="sm"
                          onClick={() => setSelectedTransactionStatus(3)}
                          disabled={selectedTransactionInfo?.status === 1}>
                          <span>
                            <SVG
                              height={16}
                              width={16}
                              src={toAbsoluteUrl(
                                "media/svg/icons/Navigation/Close.svg"
                              )}
                            />
                          </span>{" "}
                          Reject
                        </ToggleButton>
                      )}
                    </ToggleButtonGroup>
                  </li>
                  {selectedTransactionStatus === 3 ? (
                    <div className="mt-3">
                      <div className="custom-control bg-white floating-input-field-wrapper-textarea pt-7">
                        <Form.Label className="floating-input-field-label-static start-0 ms-4">
                          Reason to Reject
                          <span className="text-important ms-2 me-2">*</span>
                        </Form.Label>
                        <Form.Control
                          name="rejectReason"
                          as="textarea"
                          rows="3"
                          className={`form-control bg-transparent floating-input-field-item ${
                            modalErrors.rejectReason && "is-invalid"
                          }`}
                          {...modalRegister("rejectReason", {
                            required: intl.formatMessage({
                              id: "COMMON.FIELD.REQ",
                            }),
                          })}
                        />
                      </div>
                      <ErrorMessage
                        errors={modalErrors}
                        name="rejectReason"
                        render={({ message }) => (
                          <div className="font-size-sm text-danger ms-2">
                            {message}
                          </div>
                        )}
                      />
                    </div>
                  ) : null}
                </ul>
              </div>
              <div className="mt-5 d-flex justify-content-end gap-5 align-items-center">
                <button
                  type="button"
                  className="btn text-text-clr font-size-sm fw-medium"
                  onClick={() => handleClose()}>
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-secondary font-size-sm text-white py-2"
                  disabled={
                    selectedTransactionStatus === 0 ||
                    selectedTransactionStatus ===
                      selectedTransactionInfo?.status
                  }>
                  Confirm
                </button>
              </div>
            </Form>
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
}
export default WithdrawRequests;
